import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchSportDetail } from '@/api';
import MatchCard from '@/components/match/MatchCard';
import SEOHead from '@/components/common/SEOHead';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, Search, Filter, Trophy, BarChart3 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const FLAG_MAP = {
  TR: '🇹🇷', GB: '🇬🇧', ES: '🇪🇸', DE: '🇩🇪', IT: '🇮🇹', FR: '🇫🇷',
  NL: '🇳🇱', PT: '🇵🇹', BE: '🇧🇪', AT: '🇦🇹', CH: '🇨🇭', GR: '🇬🇷',
  DK: '🇩🇰', SA: '🇸🇦', MX: '🇲🇽', AR: '🇦🇷', BR: '🇧🇷', US: '🇺🇸',
  SE: '🇸🇪', NO: '🇳🇴', INTL: '🌍',
};

const COUNTRY_TR = {
  'Turkey': 'Türkiye', 'England': 'İngiltere', 'Spain': 'İspanya', 'Germany': 'Almanya',
  'Italy': 'İtalya', 'France': 'Fransa', 'Netherlands': 'Hollanda', 'Portugal': 'Portekiz',
  'Belgium': 'Belçika', 'Scotland': 'İskoçya', 'Austria': 'Avusturya', 'Switzerland': 'İsviçre',
  'Greece': 'Yunanistan', 'Denmark': 'Danimarka', 'Saudi Arabia': 'Suudi Arabistan',
  'Mexico': 'Meksika', 'Argentina': 'Arjantin', 'Brazil': 'Brezilya', 'USA': 'ABD',
  'Sweden': 'İsveç', 'Norway': 'Norveç', 'World': 'Uluslararası',
};

export default function SportPage() {
  const { sport, lang } = useParams();
  const { t } = useTranslation();
  const { currentLang, getLocalizedPath } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const SPORT_MAP = { futbol: 'football', fussball: 'football', football: 'football', futebol: 'football', calcio: 'football', voetbal: 'football' };
  const pathSegment = window.location.pathname.split('/').filter(Boolean)[1] || sport || '';
  const actualSport = SPORT_MAP[pathSegment] || SPORT_MAP[sport] || sport || 'football';
  const sportSlug = getLocalizedPath('football').split('/').pop();

  useEffect(() => {
    if (!actualSport) return;
    setLoading(true);
    fetchSportDetail(actualSport, { min_bookmakers: 3 })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [actualSport]);

  // Group leagues by country
  const countryGroups = useMemo(() => {
    if (!data?.leagues) return [];
    const visible = data.leagues.filter(lg => lg.visibility === 'public' && lg.compare_ready_matches > 0);
    const map = {};
    for (const lg of visible) {
      const country = lg.country || 'Other';
      if (!map[country]) {
        map[country] = {
          country,
          country_code: lg.country_code || '',
          flag: FLAG_MAP[lg.country_code] || '🏳️',
          leagues: [],
          total: 0,
        };
      }
      map[country].leagues.push(lg);
      map[country].total += lg.compare_ready_matches;
    }
    const groups = Object.values(map);
    // Turkey first, then World (intl), then by total desc
    const tr = groups.filter(g => g.country_code === 'TR');
    const intl = groups.filter(g => g.country === 'World');
    const rest = groups.filter(g => g.country_code !== 'TR' && g.country !== 'World').sort((a, b) => b.total - a.total);
    return [...tr, ...intl, ...rest];
  }, [data]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return countryGroups;
    const q = search.toLowerCase();
    return countryGroups.map(g => ({
      ...g,
      leagues: g.leagues.filter(lg =>
        lg.name.toLowerCase().includes(q) || g.country.toLowerCase().includes(q)
      ),
    })).filter(g => g.leagues.length > 0);
  }, [countryGroups, search]);

  // Compare-ready matches only
  const compareReadyMatches = useMemo(() => {
    if (!data?.matches) return [];
    return data.matches.filter(m => (m.bookmakers_count || 0) >= 3);
  }, [data]);

  if (loading) return <SportSkeleton />;
  if (!data?.sport) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('common.notFound')}</div>;

  const isTR = currentLang === 'tr';

  return (
    <div data-testid="sport-page">
      <SEOHead title={isTR ? 'Futbol Oranları ve Tahminleri | ODDSIS' : 'Football Odds & Predictions | ODDSIS'} />

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/stadium-bg.jpg" alt="" className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,6,23,0.88) 0%, rgba(2,6,23,0.75) 40%, rgba(2,6,23,0.95) 100%)' }} />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl pt-8 pb-10">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
            <Link to={`/${currentLang}`} className="hover:text-slate-200 transition-colors">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-200">{isTR ? 'Futbol' : 'Football'}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white drop-shadow-lg flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)' }}>
            {isTR ? 'FUTBOL' : 'FOOTBALL'}
          </h1>
          <p className="text-sm text-slate-300 mt-2 max-w-xl">{t('sport.subtitle', 'Compare odds, view predictions and find the best betting opportunities')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-6 space-y-8">

        {/* ═══ LEAGUES BY COUNTRY ═══ */}
        <section data-testid="leagues-list">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold tracking-tight uppercase text-slate-200" style={{ fontFamily: 'var(--font-heading)' }}>
              <Trophy className="w-5 h-5 inline mr-2 text-amber-400" />
              {isTR ? 'Ligler' : 'Leagues'}
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  placeholder={isTR ? 'Lig ara...' : 'Search league...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-8 w-44 bg-slate-900 border-slate-700 text-xs"
                  data-testid="league-search"
                />
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] shrink-0">
                <Filter className="w-3 h-3 mr-1" />{isTR ? '≥3 site' : '≥3 bookmakers'}
              </Badge>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="space-y-4" data-testid="country-groups">
              {filtered.map(group => (
                <div key={group.country} className="bg-white/[0.02] border border-slate-800/60 rounded-xl overflow-hidden" data-testid={`country-${group.country_code}`}>
                  {/* Country header */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/30 border-b border-slate-800/40">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{group.flag}</span>
                      <span className="text-sm font-semibold text-slate-200">
                        {isTR ? (COUNTRY_TR[group.country] || group.country) : group.country}
                      </span>
                    </div>
                    <Badge className="bg-slate-700/50 text-slate-400 border-slate-600/30 text-[10px]">
                      {group.total} {isTR ? 'maç' : 'matches'}
                    </Badge>
                  </div>

                  {/* League cards */}
                  <div className="divide-y divide-slate-800/30">
                    {group.leagues.map(lg => (
                      <Link key={lg.id} to={`/${currentLang}/${sportSlug}/${lg.slug}`}
                        className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/30 transition-colors group"
                        data-testid={`league-${lg.id}`}>
                        <div className="flex items-center gap-3">
                          {lg.logo_url ? (
                            <img src={lg.logo_url} alt="" className="w-7 h-7 rounded object-contain shrink-0" loading="lazy"
                              onError={e => { e.target.style.display = 'none'; }} />
                          ) : (
                            <div className="w-7 h-7 rounded bg-slate-800 flex items-center justify-center shrink-0">
                              <Trophy className="w-3.5 h-3.5 text-slate-600" />
                            </div>
                          )}
                          <span className="text-sm text-slate-300 group-hover:text-white font-medium transition-colors">{lg.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs tabular-nums font-semibold ${lg.compare_ready_matches >= 10 ? 'text-emerald-400' : lg.compare_ready_matches >= 3 ? 'text-amber-400' : 'text-slate-500'}`}>
                            {lg.compare_ready_matches} {isTR ? 'maç' : 'matches'}
                          </span>
                          <span className="text-[10px] text-slate-600 tabular-nums flex items-center gap-1">
                            <BarChart3 className="w-3 h-3" />
                            {isTR ? 'ort' : 'avg'} {lg.avg_bookmakers}
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-emerald-400 transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-900/30 border border-slate-800 rounded-xl" data-testid="empty-leagues">
              <Trophy className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">{isTR ? 'Şu an karşılaştırılabilir lig bulunamadı' : 'No leagues with comparable odds found'}</p>
              <p className="text-xs text-slate-600 mt-1">{isTR ? 'En az 3 bahis sitesinde oranı olan yaklaşan maçlar burada listelenir.' : 'Upcoming matches with odds from 3+ bookmakers will appear here.'}</p>
            </div>
          )}
        </section>

        {/* ═══ UPCOMING MATCHES ═══ */}
        <section data-testid="upcoming-matches">
          <h2 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('home.upcomingMatches')}
          </h2>
          {compareReadyMatches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {compareReadyMatches.map(match => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">{t('common.matchesScheduled')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

function SportSkeleton() {
  return (
    <div>
      <div className="bg-slate-900 py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <Skeleton className="h-5 w-32 bg-slate-800 mb-3" />
          <Skeleton className="h-12 w-48 bg-slate-800 mb-2" />
          <Skeleton className="h-4 w-96 bg-slate-800/60" />
        </div>
      </div>
      <div className="container mx-auto px-4 max-w-7xl py-6 space-y-6">
        <Skeleton className="h-6 w-32 bg-slate-800" />
        {[0, 1, 2].map(i => (
          <div key={i} className="border border-slate-800 rounded-xl overflow-hidden">
            <Skeleton className="h-10 w-full bg-slate-800/30" />
            <div className="p-2 space-y-1">
              {[0, 1].map(j => <Skeleton key={j} className="h-12 w-full bg-slate-800/20" />)}
            </div>
          </div>
        ))}
        <Skeleton className="h-6 w-40 bg-slate-800" />
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-32 bg-slate-800 rounded-xl" />)}
        </div>
      </div>
    </div>
  );
}
